import TagIcon from "assets/icons/tagIcon";
import { FileIcon } from "assets/icons/FileIcon";
import FolderIcon from "assets/icons/FolderIcon";
import UserTagIcon from "assets/icons/UserTagIcon";
import CaptionIcon from "assets/icons/CaptionIcon";
import MislabelsIcon from "assets/icons/MislabelsIcon";
import DuplicateIcon from "assets/icons/DuplicateIcon";
import OutliersIcon from "assets/icons/OutliersIcon";
import UniquenessIcon from "assets/icons/UniquenessIcon";
import QualityIssuesIcon from "assets/icons/QualityIssuesIcon";
import ObjectSizeIcon from "assets/icons/ObjectSizeIcon";

import {
  APIQueryOperators,
  FilterMenuItem,
  Filters,
  FilterSubType,
  QueryOperator,
  VqlIssuesValues,
  VqlModeOps,
  FilterMenuItemType,
} from "types";
import LabelOutliersIcon from "assets/icons/LabelOutliersIcon";
import {
  isCustomMetadata,
  customMetadataTypeIconMap,
  getCustomMetadataDisplayName,
} from "./customMetadataHelpers";

export const getOperatorTitle = (
  operator: QueryOperator,
  type?: FilterMenuItemType
): string => {
  const operatorTitles = {
    // Query operators
    [QueryOperator.IS]: "Is",
    [QueryOperator.IS_NOT]: "Is not",
    [QueryOperator.IS_ONE_OF]: "Is one of",
    [QueryOperator.IS_NOT_ONE_OF]: "Is not one of",
    [QueryOperator.ENDS_WITH]: "Ends with",
    [QueryOperator.STARTS_WITH]: "Starts with",
    [QueryOperator.CONTAINS]: "Contains",
    [QueryOperator.DOES_NOT_CONTAIN]: "Does not contain",
    [QueryOperator.GREATER_THAN]: ">",
    [QueryOperator.LESS_THAN]: "<",
    [QueryOperator.GREATER_THAN_OR_EQUAL_TO]: "≥",
    [QueryOperator.LESS_THAN_OR_EQUAL_TO]: "≤",
    [QueryOperator.BETWEEN]: "Between",
  };

  const isStringCustomMetadata =
    isCustomMetadata(type) && type.field_type === "string";

  const isSearchOperator =
    (type === Filters.CAPTION && operator === QueryOperator.IS) ||
    (isStringCustomMetadata && operator === QueryOperator.CONTAINS);

  if (isSearchOperator) return "search";

  return operatorTitles[operator] || operator;
};

export const isSignOperator = (op: string) => {
  const signOperators = ["<", ">", "≤", "≥"];
  return signOperators.includes(op);
};

export const convertAPIOperator = (
  operator: APIQueryOperators | VqlModeOps
): QueryOperator => {
  const operatorTitles: any = {
    // API Query operators
    is: QueryOperator.IS,
    is_not: QueryOperator.IS_NOT,
    one_of: QueryOperator.IS_ONE_OF,
    not_one_of: QueryOperator.IS_NOT_ONE_OF,
    contains: QueryOperator.CONTAINS,
    starts_with: QueryOperator.STARTS_WITH,
    ends_with: QueryOperator.ENDS_WITH,

    // Custom metadata operators
    eq: QueryOperator.IS,
    neq: QueryOperator.IS_NOT,
    gt: QueryOperator.GREATER_THAN,
    lt: QueryOperator.LESS_THAN,
    gte: QueryOperator.GREATER_THAN_OR_EQUAL_TO,
    lte: QueryOperator.LESS_THAN_OR_EQUAL_TO,
    between: QueryOperator.BETWEEN,

    //for issues
    in: QueryOperator.IS,
    out: QueryOperator.IS_NOT,
  };

  return operatorTitles[operator] || QueryOperator.IS; //fallback
};

export const convertApiIssueOperator = (
  operator: VqlIssuesValues
): FilterSubType => {
  const operatorTitles: any = {
    // API Query operators
    dark: FilterSubType.DARK,
    blur: FilterSubType.BLUR,
    bright: FilterSubType.BRIGHT,
  };

  return operatorTitles[operator] || FilterSubType.DARK; //fallback
};

export const getFilterSubTypeTitle = (subType: FilterSubType) => {
  const subTypeTitles = {
    [FilterSubType.BLUR]: "Blurry",
    [FilterSubType.DARK]: "Dark",
    [FilterSubType.BRIGHT]: "Bright",
  };

  return subTypeTitles[subType] || subType;
};

type Caller = "filter" | "queryTab";
export const getFilterTitle = (type: FilterMenuItemType, caller?: Caller) => {
  if (isCustomMetadata(type)) {
    return getCustomMetadataDisplayName(type.field_name);
  }

  const FILTER_DISPLAY_NAMES = {
    [Filters.FILE]: "Files",
    [Filters.LABELS]: "Labels",
    [Filters.FOLDER]: "Folders",
    [Filters.CAPTION]: "Caption",
    [Filters.OUTLIERS]: "Outliers",
    [Filters.LABEL_OUTLIER]: "Class Outliers",
    [Filters.MISLABELS]: "Mislabels",
    [Filters.USER_TAGS]: "User Tags",
    [Filters.DUPLICATES]: "Duplicates",
    [Filters.ISSUES]: "Quality Issues",
    [Filters.UNIQUENESS]: "Uniqueness",
    [Filters.SIZE]: 'Size'
  };

  if (type === Filters.UNIQUENESS && caller === "filter") {
    return "Select Uniques";
  }

  return FILTER_DISPLAY_NAMES[type as Filters] || type;
};

export const FILTER_MENU_OPTIONS: FilterMenuItem[] = [
  { icon: <FolderIcon />, type: Filters.FOLDER, entityTypes: ["IMAGES", "OBJECTS"] },
  { icon: <FileIcon />, type: Filters.FILE, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <TagIcon />, type: Filters.LABELS, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <UserTagIcon />, type: Filters.USER_TAGS, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <CaptionIcon type="IMAGE" />, type: Filters.CAPTION, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <MislabelsIcon />, type: Filters.MISLABELS, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <LabelOutliersIcon />, type: Filters.LABEL_OUTLIER, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <DuplicateIcon />, type: Filters.DUPLICATES, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <OutliersIcon />, type: Filters.OUTLIERS, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <QualityIssuesIcon />, type: Filters.ISSUES, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <UniquenessIcon />, type: Filters.UNIQUENESS, entityTypes: ["IMAGES", "OBJECTS"]  },
  { icon: <ObjectSizeIcon />, type: Filters.SIZE, entityTypes: ["OBJECTS"]  },
];

export const getFilterIcon = (
  type: FilterMenuItemType
): JSX.Element | undefined => {
  if (isCustomMetadata(type)) {
    return customMetadataTypeIconMap[type.field_type];
  }

  const filterOption = FILTER_MENU_OPTIONS.find(
    (filter) => filter.type === type
  );
  return filterOption?.icon || undefined;
};
